// THE FORM'S QUESTIONS FOLLOW THE STATE (TW-0104, 2026-09-08).
//
// A state's rule can require things on the client record that the standard form never asked: Oregon
// wants a list of sensitivities to medicines or topical solutions, a history of bleeding disorders, the
// artist's license number, a note on complications (OAR 331-915-0085). The contract is the repo root's
// `state-form-requirements.json`, generated from the 51 state records by `./run.sh state-forms`; the
// copy beside this file is byte-identical and never edited by hand. The shop's state rides on its
// public config (`state`, from the row); the demo shop takes one from `?state=` or its picker instead.
// Chris, 2026-09-08: "we should start showing the state-supported forms for each customer, by default."
//
// TW-0505 (2026-10-29) — THE PAGE NO LONGER IMPORTS THE CONTRACT: it fetches the shop's state's own slice of it,
// /_state-forms/<ST>.json, the moment the state is known (stateRules.ts has how, and what a page does while it is on its way).
// `contract` below is stateRules.ts's RULES, the same tables as the JSON's, filled in place when the state's file lands, so
// every reader here reads what it always read; until it lands a state's tables are empty, and each reader answers as it
// answers for a state with nothing extra.
import { RULES as contract, STATE_NAMES } from "./stateRules";
import { dayOf } from "./day";

export { STATE_NAMES };

export type RequirementId = string;
type Item = {
  kind: string;
  key?: string;
  details?: string;
  label?: string;
  setting?: string;
  plain: string;
  acknowledge?: boolean;
  services?: string[];
  /** TW-0526 (M04) — a question whose words ride the contract (wordedQuestionsFor), and the demo's words for it */
  words?: { en: string; es: string };
  change?: { en: string; es: string };
  /** TW-0526 (M05) — the words over the box a worded question's yes opens (`details`), Arkansas's and Hawaii's armed forces */
  details_words?: { en: string; es: string };
  /** TW-0526, the client-form side (2026-10-29) — a `field`'s control, place and words (stateFieldsFor) */
  input?: string;
  place?: string;
  everywhere?: boolean;
  missing?: { en: string; es: string };
  options?: FieldOption[];
};

const ITEMS = contract.items as Record<string, Item>;
const STATES = contract.states as Record<string, string[]>;

/** A REQUIREMENT THAT STARTS, OR ENDS, ON A DAY (TW-0222, 2026-09-24). North Dakota's Body Art Code takes effect
 *  2026-10-01 and the chapter it replaces is repealed the same day; what it adds to the form must reach a North
 *  Dakota client on that day and not a day early. The contract's `effective` carries, per state and id, the
 *  windows (product-zone days, inclusive at both ends) of every requirement that is not in force on every day it
 *  is named; an id with no windows is in force whenever the state names it. `day` is the product's day (day.ts,
 *  Eastern) the question is asked on: today's for every live signing, a given one in a test. */
export type Window = { from: string | null; until: string | null };
const EFFECTIVE = ((contract as unknown as { effective?: Record<string, Record<string, Window[]>> }).effective ?? {}) as Record<string, Record<string, Window[]>>;

const within = (w: { from?: string | null; until?: string | null }, day: string): boolean =>
  (!w.from || w.from <= day) && (!w.until || day <= w.until);

/** Is `id` in force for `state` on `day`? The date half only: whether the state names the id at all is
 *  `requirementsFor`'s question. */
export function inForce(state: string | null | undefined, id: string, day: string = dayOf()): boolean {
  const windows = EFFECTIVE[`${state ?? ""}`.trim().toUpperCase()]?.[id];
  if (!windows || !windows.length) return true;
  return windows.some((w) => within(w, day));
}

/** TW-0170 — A STATE'S OWN SENTENCE ON THE CONSENT. South Carolina's rule puts two in the client's hands at
 *  signing: the blood-donor notice, which "shall be signed by the client" (S.C. Code Regs. 60-111 § 500.C),
 *  so the client ticks it like a clause, and the grievance procedure with the Department's address and phone
 *  (§ 1002), which is words to read. The contract carries them finished, in both languages; the signed waiver
 *  prints the same words (the API reads the same contract). */
export type Notice = {
  id: string;
  acknowledge: boolean;
  services: string[];
  en: string;
  es: string;
  error_en?: string;
  error_es?: string;
  /** TW-0222 — the window of the entry that named it: shown only on a day inside it. Absent means every day. */
  from?: string;
  until?: string;
  /** TW-0526 (M03) — a statement the state's rule wants PROMINENT (Louisiana's inks, La. Admin. Code tit. 51,
   *  § XXVIII-107.A: "a prominent statement regarding the regulatory status of any inks"): the consent sets it in bold, and
   *  the signed waiver prints it in bold. Absent on every other notice, which draws as it always has. */
  prominent?: boolean;
  /** TW-0173 — a notice that carries a whole sheet (South Carolina's aftercare suggestions, which its statute says
   *  the consent "must contain", S.C. Code Ann. § 44-34-60(B)(1)): `en`/`es` is the line it sits under, `site`
   *  says to name the client's own tattoo site after that line, and the sections are the aftercare sheet's own
   *  words, drawn from services/waiver-api/consolidated_api/aftercare_words.py when the contract is generated. */
  site?: boolean;
  intro_en?: string;
  intro_es?: string;
  sections_en?: NoticeSection[];
  sections_es?: NoticeSection[];
  /** 2026-09-17 — A PLACEHOLDER THE SHOP FILLS. Georgia's consent must carry the county board of health's phone
   *  number and how to contact it (r. 511-3-8-.08(2)(f)); "Health Authority" there is the county's board, one of
   *  159, so no state record can print it and the words keep `{health_authority}` for the shop's own
   *  `public_data.health_authority` (Settings → Waiver form). Each field carries the words that stand in until the
   *  owner fills it, in both languages, so the sentence still reads as one; `noticeText` below does the filling. */
  shop_fields?: Record<string, { en: string; es: string }>;
};
/** One section of a sheet a notice carries. `procedures` (TW-0279 slice 6) is set on a section kept for some cosmetic
 *  procedures (the cosmetic sheet's brows, lips, eyeliner, areola and scalp): shown only to a signing that names one of
 *  them (sectionsFor). `placements` (TW-0526, 2026-10-28) is the same for a piercing's placements: the piercing sheet's
 *  mouth section, kept for the placements in or through the mouth (the form's posted values), the emailed sheet's rule. A
 *  section without either is every client's; the tattoo sheet has none. */
export type NoticeSection = {
  title: string;
  lead?: string | null;
  items: string[];
  tail?: string | null;
  procedures?: string[] | null;
  placements?: string[] | null;
};
const NOTICES = ((contract as unknown as { notices?: Record<string, Notice[]> }).notices ?? {}) as Record<string, Notice[]>;

/** A notice's words in the client's language, with every shop-filled placeholder replaced from the shop's public
 *  data (a trimmed string on `public_data[field]`), or by the stand-in words when the owner has not filled it. A
 *  notice with no shop fields is its words as they are — byte for byte what every consent has shown since TW-0170. */
export function noticeText(notice: Notice, lang: "en" | "es", publicData: object | null | undefined): string {
  let text = lang === "es" ? notice.es : notice.en;
  for (const [field, fallback] of Object.entries(notice.shop_fields ?? {})) {
    const raw = (publicData as Record<string, unknown> | null | undefined)?.[field];
    const own = typeof raw === "string" ? raw.trim() : "";
    text = text.split(`{${field}}`).join(own || (lang === "es" ? fallback.es : fallback.en));
  }
  return text;
}

/** A sheet's sections as THIS signing shows them, in the client's language: every client's, and a procedure's own only
 *  when the signing named one of its procedures (TW-0279 slice 6), the emailed sheet's rule, so a consent's aftercare is the
 *  client's own (New Jersey's "specific to the procedure performed", N.J.A.C. 8:27-1.3). The signed waiver prints the same
 *  sections (the API's own filter, app.py). A tattoo's sheet has no procedure section, so its sections are all of them.
 *  TW-0526 — and a section kept for some placements (the piercing sheet's mouth section) only when `placement`, the
 *  piercing's posted placement, is one of them: an earlobe's consent never reads how to rinse after eating. */
export function sectionsFor(
  notice: Notice,
  lang: "en" | "es",
  procedures: readonly string[] = [],
  placement: string = "",
): NoticeSection[] {
  const sections = (lang === "es" ? notice.sections_es : notice.sections_en) ?? [];
  const spot = `${placement ?? ""}`.trim();
  return sections
    .filter((section) => !section.procedures?.length || section.procedures.some((p) => procedures.includes(p)))
    .filter((section) => !section.placements?.length || section.placements.includes(spot));
}

/** The notices one signing shows in its consent section, in the contract's order: [] for no state, an
 *  unknown one, a state with none, or before the client has said what they are here for. Only the notices in
 *  force on `day` (TW-0222): a notice carries the window of the entry that named it. */
export function noticesFor(state: string | null | undefined, service: string | null | undefined, day: string = dayOf()): Notice[] {
  if (!service) return [];
  const abbr = `${state ?? ""}`.trim().toUpperCase();
  return (NOTICES[abbr] ?? []).filter((n) => (n.services ?? []).includes(service) && within(n, day));
}

/** The ids a state requires on `day` (today's when omitted); empty for no state, an unknown state, or a state
 *  that needs nothing extra. */
export function requirementsFor(state: string | null | undefined, day: string = dayOf()): Set<string> {
  const abbr = `${state ?? ""}`.trim().toUpperCase();
  return new Set((STATES[abbr] ?? []).filter((id) => inForce(abbr, id, day)));
}

/** TW-0526 (2026-10-28, the audit's M04) — A QUESTION A STATE'S RULE LISTS, IN THE RULE'S OWN WORDS, DRAWN BY ITS WORDS ALONE.
 *  The contract carries thirteen questions with their words in both languages (scripts/state-forms.py VOCAB, `words`): Georgia's
 *  keloid formation, Vermont's "Have you used alcohol or drugs within the last 8 hours?" and the rest. Each is the five's shape
 *  (Form.tsx askDiabetes and its siblings): a yes or a no with no box, asked where the shop's state names it on the day, refused
 *  blank on the phone and at the door (healthQuestions.ts, app.HEALTH_QUESTION_UNANSWERED), a yes recorded and never refused,
 *  printed by its label on the signed waiver. `service` is the signing's: a question naming its `services` is asked on those
 *  alone (Georgia's keloids on a tattoo, where a piercing and a cosmetic tattoo already ask keloidScarring); a page whose
 *  client has not picked asks as a tattoo does. In the contract's order. */
export type WordedQuestion = {
  id: string;
  key: string;
  en: string;
  es: string;
  /** TW-0526 (M05, 2026-10-28) — the box a yes opens and its words, where the question has one (Arkansas's armed services:
   *  "Your branch of service, and your rate or rank"); refused empty under a yes like the allergies box (healthQuestions.ts
   *  DETAILS_BOX), printed beside the yes. Absent on the thirteen, which open none. */
  details?: string;
  detailsEn?: string;
  detailsEs?: string;
};
export function wordedQuestionsFor(state: string | null | undefined, service?: string | null, day: string = dayOf()): WordedQuestion[] {
  const signing = `${service ?? ""}`.trim() || "tattoo";
  const named = requirementsFor(state, day);
  const out: WordedQuestion[] = [];
  // in the CONTRACT's order, the order the door walks for a blank (app.health_question_unanswered), so the page and the door
  // name the same first blank
  for (const [id, item] of Object.entries(ITEMS)) {
    if (!named.has(id) || item.kind !== "question" || !item.words || !item.key) continue;
    if (item.services && !item.services.includes(signing)) continue;
    const box = item.details && item.details_words ? { details: item.details, detailsEn: item.details_words.en, detailsEs: item.details_words.es } : {};
    out.push({ id, key: item.key, en: item.words.en, es: item.words.es, ...box });
  }
  return out;
}

/** The demo's words for each worded question, by its id, in the language on screen (VOCAB's `change`). */
export function wordedQuestionChangeWords(lang: "en" | "es"): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [id, item] of Object.entries(ITEMS)) {
    if (item.kind === "question" && item.words && item.change) out[id] = lang === "es" ? item.change.es : item.change.en;
  }
  return out;
}

/** Every key a worded question posts under, in any state: what a page that no longer asks one takes off the body. */
export function wordedQuestionKeys(): string[] {
  return Object.values(ITEMS).filter((item) => item.kind === "question" && item.words && item.key).map((item) => item.key as string);
}

/** TW-0526 (M05) — every box a worded question's yes opens, in any state: what a page that no longer asks the question takes off
 *  the body with it. */
export function wordedQuestionDetailsKeys(): string[] {
  return Object.values(ITEMS)
    .filter((item) => item.kind === "question" && item.words && item.key && item.details)
    .map((item) => item.details as string);
}

/** TW-0526, the client-form side (2026-10-29) — A BOX THE CLIENT FILLS, WHERE A STATE'S RULE NAMES A FACT NO YES OR NO CARRIES.
 *  Three rules want an answer that is not a yes or a no, each read at its source that night: Oklahoma's consent form asks "When
 *  the client last ate." (Okla. Admin. Code 310:233-3-6.1(b)(2)(C)), Mississippi's carries the "Sex of Client" (Rule
 *  11.5.10(1)(h)), Utah's the "client's phone number or, if the client is a minor, a parent or legal guardian's phone number"
 *  (R392-701-16(2)(c)(iv)). The contract's `field` kind (scripts/state-forms.py VOCAB, `last_ate_at` and after it) is the box,
 *  built once: `input` is the browser's own control ("time", the native picker posting HH:MM; "select", one of `options`; "tel"),
 *  `key` the body key it posts under, `place` the part of the page it sits in ("you" beside the birth date, "health" after the
 *  state's questions, "guardian" in the parent's block on a minor's route), `en`/`es` the line over it, `missingEn`/`missingEs`
 *  what the page says of a required one left empty (the signing door says the same, state_fields.py). Asked where the shop's
 *  state names the id on the day, or everywhere (`everywhere`: the parent's phone, in every state), and REQUIRED only where the
 *  state names it: `required` on the box, the browser's own stop first, the line under the box second (TW-0541). The page posts
 *  the key only where it drew the box (Form.tsx), so a body without it is a page that never drew it, which the door never refuses.
 *  One object per box and requirement, the same on every call, so a memoized box draws again only when its answer changes. */
export type FieldOption = { value: string; label: string; en: string; es: string };
export type StateField = {
  id: string;
  key: string;
  input: "time" | "select" | "tel";
  place: "you" | "health" | "guardian";
  en: string;
  es: string;
  missingEn: string;
  missingEs: string;
  options: FieldOption[];
  required: boolean;
};
const NO_OPTIONS: FieldOption[] = [];
const FIELDS_SEEN = new Map<string, StateField>();

export function stateFieldsFor(state: string | null | undefined, place: StateField["place"], day: string = dayOf()): StateField[] {
  const named = requirementsFor(state, day);
  const out: StateField[] = [];
  for (const [id, item] of Object.entries(ITEMS)) {
    if (item.kind !== "field" || item.place !== place || !item.key) continue;
    const required = named.has(id);
    if (!required && item.everywhere !== true) continue;
    const seen = `${id} ${required ? "required" : "optional"}`;
    let field = FIELDS_SEEN.get(seen);
    if (!field) {
      field = {
        id,
        key: item.key,
        input: (item.input as StateField["input"]) ?? "tel",
        place,
        en: item.words?.en ?? "",
        es: item.words?.es ?? "",
        missingEn: item.missing?.en ?? "",
        missingEs: item.missing?.es ?? "",
        options: item.options ?? NO_OPTIONS,
        required,
      };
      FIELDS_SEEN.set(seen, field);
    }
    out.push(field);
  }
  return out;
}

/** Every body key a box of the kind posts under, in any state: what a page that did not draw one takes off the body. */
export function stateFieldKeys(): string[] {
  return Object.values(ITEMS)
    .filter((item) => item.kind === "field" && item.key)
    .map((item) => item.key as string);
}

/** The boxes of the kind that take a length in the root form-field-limits.json (the typed ones: `tel`), by their body key: the
 *  form draws that number as the box's maxLength. A time picker and a list take none; the door keeps their answer in its shape. */
export function stateFieldKeysTakingALength(): string[] {
  return Object.values(ITEMS)
    .filter((item) => item.kind === "field" && item.key && item.input === "tel")
    .map((item) => item.key as string);
}

/** TW-0526, the client-form side — the demo's words for a box the client fills and for a notice whose words ride the contract
 *  (VOCAB `change`), by the id, in the language on screen: Kentucky's attestation on each service. */
export function contractChangeWords(lang: "en" | "es"): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [id, item] of Object.entries(ITEMS)) {
    // 2026-11-04 — and a setting whose words ride the contract (California's `medications_history`)
    if ((item.kind === "field" || item.kind === "notice" || item.kind === "setting") && item.change) out[id] = lang === "es" ? item.change.es : item.change.en;
  }
  return out;
}

/** Does this state's form differ from the standard one — a question asked, a setting forced, or a notice
 *  on the consent (TW-0170)? A state whose only requirements are `record` (the shop app's side) or `covered`
 *  (already on every form) reads the same form as everyone, so its demo is the plain demo. TW-0526, the client-form side —
 *  and a box the state's rule names (`field`). */
export function formDiffersIn(state: string | null | undefined, day: string = dayOf()): boolean {
  return Array.from(requirementsFor(state, day)).some((id) => {
    const kind = ITEMS[id]?.kind;
    return kind === "question" || kind === "setting" || kind === "notice" || kind === "field";
  });
}

/** The states whose form differs on `day`, for the demo shop's picker, alphabetical by name. */
export function statesWithTheirOwnForm(day: string = dayOf()): { abbr: string; name: string }[] {
  return Object.keys(STATES)
    .filter((abbr) => formDiffersIn(abbr, day))
    .map((abbr) => ({ abbr, name: STATE_NAMES[abbr] ?? abbr }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** A shop setting the state forces on (the medications question, the lot-number photos) — any item carrying the
 *  setting, whatever its kind: since TW-0293 (2026-10-15) a `covered` question every form used to ask is kept on by a
 *  state that names it (not_intoxicated → ask_drugs_or_alcohol, communicable_disease → ask_communicable_disease). */
export function settingForcedBy(state: string | null | undefined, setting: string, day: string = dayOf()): boolean {
  return Array.from(requirementsFor(state, day)).some((id) => ITEMS[id]?.setting === setting);
}

/** 2026-11-04 (a California shop's ask) — IS THE ONE QUESTION A CLIENT MAY LEAVE BLANK A YES OR A NO IN THIS STATE? Every form asks
 *  "Are you pregnant or nursing?" and lets it go blank (healthQuestions.ts OPTIONAL_HEALTH_QUESTIONS); a state whose record names
 *  an item carrying `answered: <that key>` (scripts/state-forms.py VOCAB `pregnancy_answered`: California's "Whether the client
 *  may be pregnant.", Cal. Health & Safety Code § 119303(b)(1)) makes it required, on the radios and at the submit, as the door
 *  does (app.health_question_unanswered, state_forms.answer_required_by). False for no state, an unknown one, and any other. */
export function answerRequiredBy(state: string | null | undefined, key: string, day: string = dayOf()): boolean {
  return Array.from(requirementsFor(state, day)).some((id) => (ITEMS[id] as { answered?: string } | undefined)?.answered === key);
}

/** 2026-11-04 (a California shop's ask) — THE MEDICATIONS BOX'S WORDS WHERE THE STATE'S RECORD WORDS IT. California's "Whether the
 *  client has a history of medication use or is currently using medication" (Cal. Health & Safety Code § 119303(b)(3)) asks the
 *  past as well as the present; its record names `medications_history`, a `setting` carrying the box's sentence for each signing
 *  in both languages (scripts/state-forms.py VOCAB). The sentence for this signing (a page whose client has not picked reads as
 *  a tattoo, as i18n's own does), or null where no state item words it, and the page says its own (i18n `medications`). */
export function medicationsWordsFor(
  state: string | null | undefined,
  service: string | null | undefined,
  lang: "en" | "es",
  day: string = dayOf()
): string | null {
  const signing = service === "piercing" || service === "cosmetic" ? service : "tattoo";
  for (const id of Array.from(requirementsFor(state, day))) {
    const item = ITEMS[id];
    if (item?.kind !== "setting" || item.setting !== "ask_more_questions_about_medications_and_blood_thinners") continue;
    const words = (item as unknown as { words?: Record<string, { en: string; es: string }> }).words;
    const pair = words?.[signing];
    if (pair) return lang === "es" ? pair.es : pair.en;
  }
  return null;
}

/** The contract's `duties` (TW-0215): what each state's rule has the shop DO with the signed record, one entry per rule, each
 *  carrying its own window (TW-0222: North Dakota's copy rule is two entries, one each side of 2027-01-01). */
type DutyEntry = { id: string; from?: string | null; until?: string | null };
const DUTIES = ((contract as unknown as { duties?: Record<string, DutyEntry[]> }).duties ?? {}) as Record<string, DutyEntry[]>;

/** 2026-10-08 — A DUTY, READ BY THE FORM FOR THE FIRST TIME (the client's own copy on a HIPAA shop's done screen,
 *  clientCopy.ts). Does `state`'s rule name the `duty`-kind item `id` on `day`? The API's own answer as a yes or a no:
 *  state_forms.duty_for, which client_copy.required_by_rule reads. Three halves, each the API's: the state requires the id
 *  on that day (requirementsFor, windows and all), the vocabulary says its kind is `duty`, and the contract's `duties`
 *  carries an entry for it in force on that day. False for no state, an unknown state, an id of any other kind and an id
 *  the state does not name. Only whether: the rule's own words and cite are the shop's to read, never the client's. */
export function dutyNamedBy(state: string | null | undefined, id: string, day: string = dayOf()): boolean {
  const abbr = `${state ?? ""}`.trim().toUpperCase();
  const wanted = `${id ?? ""}`.trim();
  if (!wanted || ITEMS[wanted]?.kind !== "duty" || !requirementsFor(abbr, day).has(wanted)) return false;
  return (DUTIES[abbr] ?? []).some((entry) => entry.id === wanted && within(entry, day));
}

/** Does "Choose from library" on the ID photo START ON for a shop that has not said? YES, EVERYWHERE, since
 *  2026-10-01 (Chris: "i think we should have it on by default for states that support it." · "it sounds like
 *  the 21 can be expanded if it's allowed?"). A saved photo is the COPY of the ID a shop's file keeps, never the
 *  proof of age (that is the artist looking at the card, the rule in every state and our terms' §3), and every
 *  state's copy rule takes a saved photo the same as the camera's shot: 50 records want a copy in the record
 *  (Vermont none), none says who makes it, and the "photocopy" states (Arkansas, Mississippi "of an original",
 *  Oklahoma, New Jersey for minors, New Hampshire "a photocopy, photograph, or scan") already apply to the camera
 *  path the same way. The owner's own switch on the row wins either way (Form.tsx). The contract's `digital_id`
 *  (where a state-issued digital license is a real ID) is what Settings shows beside the box, not a gate. For an
 *  hour tonight it started on in those 21 states only; from 2026-09-11 to then it was opt-in everywhere.
 *  One function, so a state that ever forbids a saved copy gets its exception HERE and in the API's twin,
 *  state_forms.id_photo_library_starts_on, and nowhere else. */
export function idPhotoLibraryStartsOn(): boolean {
  return true;
}

/** The demo shop's state, from the address bar: `?state=OR`. Only the demo reads it — a real shop's
 *  form is its own state's, never a visitor's query string. */
export function demoStateFromUrl(search: string): string | null {
  const v = new URLSearchParams(search).get("state");
  if (!v) return null;
  const abbr = v.trim().toUpperCase();
  return STATE_NAMES[abbr] ? abbr : null;
}

/** What a state changes on the FORM itself, in the contract's order: the questions it adds, the settings it
 *  forces on, the notices on its consent (TW-0170), and the license number beside the artist. `record` items the shop app carries and `covered` items
 *  already on every form are not changes a client would see, so they are not listed. */
export function formChangesFor(state: string | null | undefined, services?: string[], day: string = dayOf()): string[] {
  return Array.from(requirementsFor(state, day)).filter((id) => {
    const item = ITEMS[id];
    const kind = item?.kind;
    // TW-0526, the client-form side — a box the client fills is a change to the form a client sees, like a question
    const listed = kind === "question" || kind === "setting" || kind === "notice" || kind === "field" || id === "artist_license_number";
    if (!listed) return false;
    // A notice the contract writes for ONE service (a tattoo's risks, a tattoo's aftercare suggestions) is not a
    // change to the other service's form. `services` is what the asking shop does; absent means every service.
    if (services && item?.services?.length && !item.services.some((s) => services.includes(s))) return false;
    return true;
  });
}

/** Every id `formChangesFor` can ever return, whatever the state: what the demo's words are held to (demo.test.ts). */
export function everyFormChangeId(): string[] {
  return Object.keys(ITEMS).filter((id) => {
    const kind = ITEMS[id]?.kind;
    return kind === "question" || kind === "setting" || kind === "notice" || kind === "field" || id === "artist_license_number";
  });
}

/** The demo shop pierces minors, from the address bar: `?minors=1`. Demo-only, like `?state=` — a
 *  real shop's answer is its own setting (TW-0128), and this is how the whole guardian route can be
 *  walked, screenshotted and linked from a state page without turning it on for anybody real. */
export function demoMinorsFromUrl(search: string): boolean {
  const v = `${new URLSearchParams(search).get("minors") ?? ""}`.trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

/** 2026-09-24 — the demo shop asks for the birth certificate or guardianship papers when a parent signs:
 *  `?proof=1`. Demo-only, like `?minors=`, and it means nothing without that one: the guardian route is
 *  where the photo is asked. The reply to a shop owner's question carries the demo with this on. */
export function demoProofFromUrl(search: string): boolean {
  const v = `${new URLSearchParams(search).get("proof") ?? ""}`.trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

/** TW-0227 — the suffixes a county's name carries as Google writes administrative_area_level_2: the generator's
 *  COUNTY_SUFFIXES (scripts/state-forms.py), for the one reader here that has to tell a county from a city by its name,
 *  the demo's place dial below. Nothing that decides a real shop's rule reads a name this way: a real row carries the
 *  kind Google answered with. */
const COUNTY_SUFFIXES = [" County", " Parish", " Borough", " Census Area", " Municipality", " City and Borough"];

export type DemoPlace = { county: string | null; countyKind: string | null; city: string | null };
export const NO_DEMO_PLACE: DemoPlace = { county: null, countyKind: null, city: null };

/** A place name off the address bar, or null: letters, spaces, periods, apostrophes and hyphens, at most 60 — the
 *  shape of every county and city name a state record matches, and nothing a link could smuggle through. */
function placeName(raw: string | null): string | null {
  const v = `${raw ?? ""}`.split(/\s+/).filter(Boolean).join(" ");
  return v && v.length <= 60 && /^[A-Za-z][A-Za-z .'-]*$/.test(v) ? v : null;
}

/** TW-0227 — WHERE IN ITS STATE the demo shop is, from the address bar: `?county=Lake%20County&city=Hammond`, so the
 *  demo can show a shop in a jurisdiction whose rule is its own — Hammond's both parents, Baltimore City's ban
 *  (`?state=MD&county=Baltimore`), Clark County's floor. Demo-only, like `?state=`, and NOTHING without a state: a
 *  county belongs to its state, and a leftover county under another state would be a jurisdiction that does not
 *  exist. The kind follows from the name the way Places answers: a name that reads as a county is
 *  administrative_area_level_2, anything else an independent city (which carries no city of its own). */
export function demoPlaceFromUrl(search: string): DemoPlace {
  if (!demoStateFromUrl(search)) return NO_DEMO_PLACE;
  const params = new URLSearchParams(search);
  const county = placeName(params.get("county"));
  if (!county) return NO_DEMO_PLACE;
  const isCounty = COUNTY_SUFFIXES.some((suffix) => county.endsWith(suffix));
  return {
    county,
    countyKind: isCounty ? "county" : "city",
    city: isCounty ? placeName(params.get("city")) : county,
  };
}

/** What a person reads for the demo's place: "Hammond, Lake County", "Baltimore", or "" for none. */
export function demoPlaceLabel(place: DemoPlace): string {
  if (!place.county) return "";
  return place.city && place.city !== place.county ? `${place.city}, ${place.county}` : place.county;
}

/** TW-0225 — the demo client is here for an ear piercing: `?ear=1`. Demo-only, like `?proof=`, and it means nothing
 *  without `?minors=`: the ear question is asked of a minor's piercing, where the state writes the ear out of its rule.
 *  The demo form starts a piercing's spot at Earlobe, and the line under the dials says what the state does with one. */
export function demoEarFromUrl(search: string): boolean {
  const v = `${new URLSearchParams(search).get("ear") ?? ""}`.trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

/** The demo shop's kind, from the address bar: `?type=both`. Demo-only, like `?state=`. TW-0279 — and `?type=cosmetic`, a
 *  cosmetic studio (App.tsx withDemo sets `cosmetic: "only"`), a link's dial while the cosmetic form ships dark. */
export function demoTypeFromUrl(search: string): "tattoo" | "piercing" | "both" | "cosmetic" | null {
  const v = `${new URLSearchParams(search).get("type") ?? ""}`.trim().toLowerCase();
  return v === "tattoo" || v === "piercing" || v === "both" || v === "cosmetic" ? v : null;
}

/** THE DEMO'S CONFIGURATION, TO CARRY ONTO ANOTHER PAGE (TW-0138). The four dials as a query
 *  string, each one round-tripped through its own parser above rather than copied — so a bogus
 *  `?state=ZZ` drops, `?minors=yes` normalises to `minors=1`, and nothing else that happens to be
 *  on the incoming URL (a utm, somebody's `?redirect=`) can be laundered through onto a link we
 *  render. Empty string when the demo is sitting at its defaults, which is most of the time. */
export function demoQuery(search: string): string {
  const out = new URLSearchParams();
  const state = demoStateFromUrl(search);
  if (state) out.set("state", state);
  const type = demoTypeFromUrl(search);
  if (type) out.set("type", type);
  if (demoMinorsFromUrl(search)) out.set("minors", "1");
  if (demoProofFromUrl(search)) out.set("proof", "1");
  // TW-0227 — and where in the state, round-tripped through its own parser like the rest
  const place = demoPlaceFromUrl(search);
  if (place.county) out.set("county", place.county);
  if (place.city && place.countyKind === "county") out.set("city", place.city);
  if (demoEarFromUrl(search)) out.set("ear", "1");
  return out.toString();
}

/** `href` with those dials on it, JOINED through URLSearchParams rather than concatenated: the
 *  hrefs this is used on already carry `?lang=es` half the time, and `href + "?" + extra` puts a
 *  second `?` in the middle of one. The href's own value wins on a collision — it is the page we
 *  are deliberately pointing at — and an href with nothing to carry comes back untouched, which is
 *  what keeps a real shop's terms URL byte-for-byte what it has always been. */
export function withDemoQuery(href: string, search: string): string {
  const extra = demoQuery(search);
  if (!extra) return href;
  const hashAt = href.indexOf("#");
  const hash = hashAt === -1 ? "" : href.slice(hashAt);
  const [path, existing = ""] = (hashAt === -1 ? href : href.slice(0, hashAt)).split("?");
  const params = new URLSearchParams(existing);
  new URLSearchParams(extra).forEach((value, key) => {
    if (!params.has(key)) params.append(key, value);
  });
  return `${path}?${params.toString()}${hash}`;
}

// STATE_NAMES, the 51 by name, lives in stateRules.ts since TW-0505 (each is a file the page can fetch) and is re-exported
// above, so every import of it from here reads what it always read.
